// Portal tasks — which of the studio's task fields a given space offers.
//
// The Work task module is mounted UNMODIFIED in Personal, Owners and Barbara
// (see SpaceTaskBody). That was the right call for the mount — it kept Work
// provably unchanged — but it left the studio's vocabulary sitting inside three
// private spaces: a household chore could be filed under a studio project, and
// a caregiving task could be linked to a voice student. This is the narrowing
// pass canon called for, and this file is the entire policy behind it.
//
// ONLY THE FIELDS ACTUALLY TURNED OFF LIVE HERE. `area` and `assignee` stay on
// everywhere by Anthony's call (2026-07-30): area because the grouping earns
// its keep until a user-defined select replaces it, assignee because Grace is a
// genuine collaborator in all three private spaces (shared household, co-owner,
// shared caregiving) — unlike a project or a student, assignment there is real.
// They are deliberately NOT listed as always-true flags. A flag no code
// branches on is a lie that reads as tested: it looks like a supported switch
// and would break the first time someone flipped it. Turning one of them off is
// a new change, with its own gating and its own tests.
//
// UNKNOWN SPACES GET THE RESTRICTIVE ANSWER, and the direction is the point. A
// space added later and forgotten here renders WITHOUT the studio fields, so
// the failure mode is a missing field someone asks for — visible, harmless, one
// line to fix. The permissive default fails the other way: studio vocabulary
// leaks silently into a private space, which is precisely the bug this file
// exists to remove.

/* TWO OF THESE ARE SWITCHES AND TWO ARE NARROWINGS, and the difference is why
   they do not share a shape. `project` and `student` are gone entirely from a
   private space. `people` and `gates` still exist there — a private task really
   does get assigned, and really can be parked — but the SET on offer is wrong.
   Forcing a narrowing into a boolean would have meant either losing a field that
   is genuinely useful or keeping options that are meaningless, and the comment
   above about flags that no code branches on cuts the same way here: a value
   that lies about its own kind is worse than a value that is merely wrong. */

/* AREAS ARE A THIRD KIND AGAIN — a REPLACEMENT, not a switch and not a
   narrowing, and that difference is the whole reason they could not be modelled
   like `people` or `gates`.
   `isPersonOffered` / `isGateOffered` FILTER a global list: every option a
   private space offers is already in the canon vocabulary, so narrowing is
   subtraction. Areas are not. "Home" and "Medical" do not exist in
   `taxonomy.areas` at all, so filtering the studio seven can only ever yield
   studio keys — a private space needs its own list SUBSTITUTED, not trimmed.

   WHY THESE LIVE HERE AND NOT IN `src/canon/taxonomy.json`. Canon is published
   to Firestore `config/taxonomy` by scripts/publishCanon.js and read by the
   triage rules and any agent that files a task. Household and caregiving
   vocabulary in that document would invite a Work task to be filed under
   "Home", and would put Barbara's caregiving words in a doc the studio shares.
   The studio taxonomy stays studio-only; this file is already the entire policy
   behind what a space offers, so the private vocabulary belongs beside it.

   RESOLUTION IS STILL GLOBAL — see taskModel.js, which merges these into
   `AREAS` so `areaMeta()` can label them. That split is the same lesson
   `isPersonOffered` records: OFFERING and RESOLVING are different questions.
   Here it runs the other way. Narrowing what is offered must not narrow what is
   resolved; ADDING an offering must also add its resolution, or the key is
   selectable and then renders as "No area" in neutral grey everywhere it
   appears — picker, chip, row, board header, aria-label. */
export const PRIVATE_AREAS = Object.freeze({
	/* Personal deliberately has no "Errands" (Anthony, 2026-07-31): the shared
	   household shopping list lives in Apple Reminders, and a portal area for it
	   would only be a second place to look. */
	personal: Object.freeze([
		Object.freeze({ key: 'home', label: 'Home' }),
		Object.freeze({ key: 'money', label: 'Money' }),
	]),
	owners: Object.freeze([
		Object.freeze({ key: 'pay', label: 'Pay' }),
		Object.freeze({ key: 'payroll', label: 'Payroll' }),
		Object.freeze({ key: 'taxes', label: 'Taxes' }),
		Object.freeze({ key: 'classification', label: 'Classification' }),
	]),
	/* "Paperwork" rather than the space's older "Statements" sub-heading: both
	   live Barbara tasks (pension papers, the Yonkers safe-deposit box) are
	   paperwork and neither is a bill or an appointment. */
	barbara: Object.freeze([
		Object.freeze({ key: 'bills', label: 'Bills' }),
		Object.freeze({ key: 'appointments', label: 'Appointments' }),
		Object.freeze({ key: 'medical', label: 'Medical' }),
		Object.freeze({ key: 'paperwork', label: 'Paperwork' }),
	]),
});

/** Every studio field, on. The Work space, and the fallback for any component
    rendered outside a space (a direct mount, a test harness) — those used to see
    the full set and must keep seeing it. */
export const STUDIO_FIELDS = Object.freeze({
	project: true,
	student: true,
	/** 'all' — the whole canon roster is assignable. */
	people: 'all',
	/** null — the whole canon gate vocabulary is on offer. */
	gates: null,
	/** null — the whole canon area vocabulary (taxonomy.areas) is on offer. */
	areas: null,
});

/* What every private space shares. It used to be the WHOLE policy — one frozen
   object served all three — and it stopped being enough the moment areas became
   per-space: Personal's "Home / Money" is not Barbara's "Bills / Appointments /
   Medical / Paperwork". The shared part is still shared; only `areas` differs. */
const PRIVATE_BASE = Object.freeze({
	project: false,
	student: false,
	/* Annelise is `owner: false` in canon, and these collections are walled by
	   isOwner() — deliberately NOT isAdmin(). Offering her was not cosmetic: the
	   write SUCCEEDS and then the document is unreadable to the person it was
	   assigned to, with no error anywhere. A picker must not offer a choice the
	   rules will make meaningless. */
	people: 'owners',
	/* Of the three canon gates, only `blocked-decision` survives translation to a
	   household or caregiving task — nothing there waits on a deploy, and
	   `cowork` names a studio working arrangement. Parking on a pending decision
	   is genuinely useful, so the FIELD stays and only the options narrow. */
	gates: Object.freeze(['blocked-decision']),
});

/** A private space's fields = the shared base + its own area vocabulary. */
function privateFields(spaceKey, overrides = {}) {
	return Object.freeze({
		...PRIVATE_BASE,
		areas: PRIVATE_AREAS[spaceKey],
		...overrides,
	});
}

/* PERSONAL GETS `project` BACK, AND THIS IS NOT A REVERT OF THE 2026-07-30
   NARROWING. That narrowing turned the field off because the only projects on
   offer were STUDIO projects: filing a household chore under "Website / Portal
   Build" was the bug. Personal now has its own `personalProjects` collection
   (owner-walled, isOwner(), like every other Personal collection), so the field
   means something different than it did — and the reason it is safe is the same
   reason it was unsafe before: which collection it points at.

   The pointer is NOT here. `project: true` says only "this space has projects";
   WHICH collection comes from ProjectServiceContext, exactly as the task
   collection does. Encoding a collection name in this file would put the wall
   in two places, and a wall in two places is a wall in neither.

   Owners and Barbara are deliberately NOT given this. They have no project
   collection, and an unknown space gets the restrictive answer — so the failure
   mode if one is added later and forgotten is a missing field, not a household
   space quietly listing studio work. */

/* The answer for a space this file has never heard of. Restrictive in the same
   direction as everything above, and `areas: []` is the deliberate value: an
   unknown space renders an area select with nothing in it — visible, harmless,
   one line to fix — rather than inheriting the studio seven, which is the
   silent leak this file exists to prevent. Distinct from STUDIO_FIELDS, which
   remains the default for a component rendered OUTSIDE any space (a direct
   mount, a test harness); those must keep seeing the full set. */
const UNKNOWN_FIELDS = Object.freeze({ ...PRIVATE_BASE, areas: Object.freeze([]) });

/**
 * Is this person offered by the assignee pickers in this space?
 *
 * A PREDICATE, not a filtered roster, on purpose: `PEOPLE_BY_UID` must keep
 * resolving every uid for DISPLAY, or a task already assigned to someone would
 * render as an unknown teammate the moment they stopped being offered. Offering
 * and resolving are different questions and this only answers the first.
 */
export function isPersonOffered(fields, person) {
	if (!fields || fields.people !== 'owners') return true;
	return person?.owner === true;
}

/** Is this gate key offered by the pickers in this space? `gates: null` = all. */
export function isGateOffered(fields, gateKey) {
	if (!fields || !fields.gates) return true;
	return fields.gates.includes(gateKey);
}

const BY_SPACE = Object.freeze({
	work: STUDIO_FIELDS,
	personal: privateFields('personal', { project: true }),
	owners: privateFields('owners'),
	barbara: privateFields('barbara'),
});

/**
 * The studio fields a space offers.
 *
 * @param {string} space a task service's `space` ('work' | 'personal' | …)
 * @returns {{project: boolean, student: boolean, people: string,
 *   gates: string[]|null, areas: {key: string, label: string}[]|null}} frozen;
 *   safe to use as a render-stable dependency, since every space maps to one of
 *   a fixed set of module-level constants.
 */
export function spaceFields(space) {
	return BY_SPACE[space] || UNKNOWN_FIELDS;
}

/**
 * The userPrefs namespace holding a space's board preferences.
 *
 * This used to be the bare string 'workBoard' for every space, which is how the
 * card-property chooser came to bleed: `userPrefs/{uid}` is ONE document, so
 * Personal and Work were writing the same field, and turning a property off in
 * one turned it off in the other. saveMyPrefs merges by namespace, so distinct
 * namespaces simply coexist in that document.
 *
 * WORK'S NAMESPACE IS UNCHANGED, deliberately — 'work' + 'Board' is the exact
 * string it already wrote, so no existing preference has to be migrated and
 * nobody's board resets. That is the whole reason the space key is the prefix
 * rather than something tidier.
 *
 * @param {string} space a task service's `space`
 */
export function boardPrefsNamespace(space) {
	return `${space || 'work'}Board`;
}

export default spaceFields;
